import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail } from "lucide-react";
import { SENDER, TEMPLATES, firstName, subjectFor, buildGmailUrl } from "@/lib/consultantEmail";

export default function DraftEmailDialog({ open, onOpenChange, projectId, project, service, consultant, assignedEmail, onSent }) {
  const quoteDefault = consultant?.quote_needed === "start" ? "start" : "ask";
  const [intent, setIntent] = useState("ask");   // ask | start
  const [template, setTemplate] = useState("request_quote");
  const [body, setBody] = useState("");

  const subject = useMemo(() => subjectFor(project, service), [project, service]);
  const to = (consultant?.emails || []).join(", ");
  const cc = [assignedEmail].filter(Boolean).join(", ");
  const fname = firstName(consultant?.contacts, consultant?.company);

  useEffect(() => {
    if (open) {
      const d = quoteDefault;
      setIntent(d);
      const tpl = d === "start" ? "go_ahead" : "request_quote";
      setTemplate(tpl);
      setBody(TEMPLATES[tpl].body(fname, service));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickIntent = (v) => {
    setIntent(v);
    const tpl = v === "start" ? "go_ahead" : "request_quote";
    setTemplate(tpl);
    setBody(TEMPLATES[tpl].body(fname, service));
  };
  const pickTemplate = (v) => { setTemplate(v); setBody(TEMPLATES[v].body(fname, service)); };

  const openInGmail = async () => {
    if (!to) { toast.error("This consultant has no email on file"); return; }
    const url = buildGmailUrl({ to, cc, subject, body });
    window.open(url, "_blank");
    if (!consultant?.row_id || !projectId) { onOpenChange(false); return; }
    try {
      const mode = TEMPLATES[template].mode;
      await api.post(`/projects/${projectId}/consultants/${consultant.row_id}/email-drafted`, { mode, template: TEMPLATES[template].label });
      toast.success("Opened in Gmail — row updated");
      onSent && onSent();
      onOpenChange(false);
    } catch { onOpenChange(false); }
  };

  const L = "text-xs font-semibold uppercase tracking-wider text-zinc-500";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-y-auto" data-testid="draft-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Draft email · {service}</DialogTitle>
          <DialogDescription>{consultant?.company || "No consultant selected"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <label className={L}>What are we asking for?</label>
            <div className="flex gap-2 mt-1.5" data-testid="email-intent-switch">
              {[["ask", "Get a quote first"], ["start", "Start the work now – no quote"]].map(([v, l]) => (
                <button key={v} onClick={() => pickIntent(v)} data-testid={`intent-${v}`}
                  className={`flex-1 px-3 py-2 rounded-sm text-sm font-semibold border transition-colors ${intent === v ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div><span className={L}>To</span><p className="mt-0.5 text-zinc-800 break-all" data-testid="email-to">{to || <span className="text-rose-500">No email on file</span>}</p></div>
            <div><span className={L}>CC</span><p className="mt-0.5 text-zinc-600 break-all">{cc}</p></div>
            <div><span className={L}>Subject</span><p className="mt-0.5 font-semibold text-zinc-900" data-testid="email-subject">{subject}</p></div>
          </div>
          <div>
            <label className={L}>Template</label>
            <Select value={template} onValueChange={pickTemplate}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="email-template"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TEMPLATES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className={L}>Body</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="mt-1.5 rounded-sm text-sm" data-testid="email-body" />
          </div>
        </div>
        <DialogFooter className="flex-col items-stretch sm:flex-col sm:items-stretch gap-2">
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={openInGmail} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2" data-testid="open-in-gmail">
              <Mail className="h-4 w-4" /> Open in Gmail
            </Button>
          </div>
          <p className="text-[11px] text-zinc-400 text-right" data-testid="gmail-sender-note">
            Sends from {SENDER}. If Gmail opens in another account, sign in to info@ first.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
